import { ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useConfig } from "../contexts/ConfigContext";
import { registerNotifications } from "../helper/notifications";
import { patchLang } from "../services/user.service";
import { useResonanceData } from "../components/organisms/ResonanceContext/ResonanceDataContext";
import { INLINE_SEQUENCE_MAP } from "../pages/MentalPlusPages/IslandRegistry";

const ResonanceInit = (): ReactElement => {
  const { user } = useAuth();
  const { study } = useConfig();
  const { i18n } = useTranslation();
  const { fetchSequences, fetchScores, prefetchFullSequencesForStudySequenceIds } =
    useResonanceData();
  // const { setResonanceLayout, userGamificationDetails } = useResonanceLayout();

  const [publicKey, setPublicKey] = useState<string | null>(null);

  // Set app language if user is authenticated and has a language set
  useEffect(() => {
    if (user?.lang) {
      i18n.changeLanguage(user.lang);
    }
  }, [user?.lang, i18n]);

  // Patch language to backend (only if user is authenticated)
  useEffect(() => {
    if (user && study?.id && i18n.language) {
      patchLang(study.id, { lang: i18n.language });
    }
  }, [user, study?.id, i18n.language]);

  // Load public key (for notifications) - doesn't need user, just study
  useEffect(() => {
    if (!study.id) return;

    const path = `/assets/keys/${study.id}-web-key.json`;

    fetch(path)
      .then((res) => res.json())
      .then((data) => setPublicKey(data?.public_key || null))
      .catch((err) => {
        console.error("Failed to load web push key", err);
        setPublicKey(null);
      });
  }, [study.id]);

  // Register notifications only if user is authenticated
  useEffect(() => {
    if (user && study.id && publicKey) {
      registerNotifications({ studyId: study.id, public_key: publicKey });
    }
  }, [user, study.id, publicKey]);

  // ✅ Main init: scores + sequences + prefetch inline FullSequence
  useEffect(() => {
    if (!user || !study?.id) return;

    (async () => {
      await fetchScores();

      const sequencesList = await fetchSequences();
      if (!sequencesList?.length) {
        console.warn("[ResonanceInit] fetchSequences returned empty, skipping prefetch");
        return;
      }

      const studySequenceIds = Array.from(
        new Set(Object.values(INLINE_SEQUENCE_MAP).filter(Boolean))
      ) as number[];

      console.log("[ResonanceInit] INLINE studySequenceIds to prefetch:", studySequenceIds);

      // ✅ IMPORTANT: pass sequencesList so prefetch does not rely on state timing
      await prefetchFullSequencesForStudySequenceIds(studySequenceIds, sequencesList);

      console.log("[ResonanceInit] Prefetch requested for studySequenceIds:", studySequenceIds);
    })();
  }, [user, study?.id, i18n.language]);

  // // Gamification setup (uncomment and use only if user is authenticated)
  // useEffect(() => {
  //   if (user && !userGamificationDetails && study?.id && user?.id) {
  //     postUserGamification(study.id, user.id, null)
  //       .then(({ data }) => {
  //         if (data) {
  //           const updates: UserGamificationDetails = {
  //             points: data.points,
  //             step: data.step,
  //             animation_success: data.animation_success,
  //             pointsEarned: 0,
  //             stepEarned: 0,
  //             animation_successEarned: 0,
  //           };
  //           setResonanceLayout({ userGamificationDetails: updates });
  //         }
  //       })
  //       .catch((err) => {
  //         console.error("Failed to fetch user gamification", err);
  //       });
  //   }
  // }, [user, study?.id, userGamificationDetails, setResonanceLayout]);

  return null;
};

export default ResonanceInit;
